import { LoaderFunctionArgs, Outlet, useNavigate, } from "react-router-dom";
import { Container, } from "@chakra-ui/react";
import { usePersistStore, } from "../store/persistStore";
import { appendReturnTo, EJWTStatus, redirectSafe, validateJWT, } from "../utils";
import { RoutesMap, } from "../routes";
import { NetworkError, } from "../api";
import { useQueryClient, } from "@tanstack/react-query";
import { useEffect, } from "react";
import { AppModalProvider, } from "../components/features/Modals";
import { TokenRefresher, } from "../components/shared/TokenRefresher";
import { useFlag, } from "@unleash/proxy-client-react";
import { MaintenancePage, } from "../components/features/MaintenancePage";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { MODERATOR_LOCALSTORAGE_KEYS, useModeratorStore, } from "../store/moderatorStore.ts";
import { VITE_MODERATOR_CODE, VITE_MODERATOR_PHONE, } from "../ENV.ts";

/**
 * Main Layout of the application. Present as a root component for an application.
 */
export function MainLayout() {
  const queryClient = useQueryClient();
  const clearToken = usePersistStore(state => state.clearJwt,);
  const navigate = useNavigate();
  const isOnMaintenance = useFlag("maintenance",);

  useEffect(() => {
    return queryClient.getQueryCache().subscribe((event,) => {
      if (event.type === "updated" && event.action.type === "error") {
        if ((event.action.error as Error).name === "NetworkError") {
          const networkError = event.action.error as NetworkError;
          switch (networkError.internalCode) {
            case 10000: {
              // 500 Internal Server Error
              throw networkError; // To Router Error Boundary
            }
            case 1001: {
              // 401 Unauthorized
              clearToken();
              navigate(RoutesMap.login.index,);
              return;
            }
            default: {
              console.error(networkError,);
              return;
            }
          }
        }
      }
    },);
  }, [],);

  if (isOnMaintenance) {
    return (
      <Container>
        <MaintenancePage />
      </Container>
    );
  }

  return (
    <AppModalProvider>
      <TokenRefresher />
      <Container>
        <Outlet />
      </Container>
    </AppModalProvider>
  );
}

/**
 * JWT Session behavior.
 * * Invalid - should be redirected to the login page;
 * * RefreshRequired - token should be refreshed;
 * * Valid - do nothing;
 * * Expired - the token is expired but present and can be refreshed by API
 */
MainLayout.loader = async ({ request, }: LoaderFunctionArgs,) => {
  const initModeratorState = useModeratorStore.getState().init;
  const state = usePersistStore.getState();
  const deviceJwtState = validateJWT(state.deviceJWT,);
  const currentUrl = new URL(request.url,);
  const returnTo = `${currentUrl.pathname}${currentUrl.search}`;
  const loginUrl = appendReturnTo(RoutesMap.login.index, returnTo,);
  const pinUrl = appendReturnTo(RoutesMap.login.pin, returnTo,);

  const fp = await FingerprintJS.load();
  state.setFingerprint((await fp.get()).visitorId,);

  switch (deviceJwtState) {
    case EJWTStatus.RefreshRequired:
    case EJWTStatus.Expired: {
      return redirectSafe(pinUrl, request.url, RoutesMap.login.pin,);
    }
    case EJWTStatus.Invalid: {
      // Only for moderator -->
      const phone = localStorage.getItem(MODERATOR_LOCALSTORAGE_KEYS.phone,) ?? "";
      const code = localStorage.getItem(MODERATOR_LOCALSTORAGE_KEYS.code,) ?? "";
      if (phone === VITE_MODERATOR_PHONE && code === VITE_MODERATOR_CODE) {
        initModeratorState(phone,);
        return null;
      }
      // <-- Only for moderator

      state.clearDeviceJwt();
      return redirectSafe(loginUrl, request.url, RoutesMap.login.index,);
    }
    case EJWTStatus.Valid:
      return null;
  }
};
